import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { ApiError } from '@awj/api-client';
import { formatDate } from '@awj/i18n';
import { Skeleton, useToast } from '@awj/ui';

import { EmptyState } from '~/components/EmptyState.tsx';
import { FormPage } from '~/components/FormPage.tsx';
import { FieldHead } from '~/components/InfoHint.tsx';
import { useI18n } from '~/i18n/index.tsx';
import { useReplyToReview, useReviews } from '~/lib/inbox-queries.ts';

/**
 * The shop's answer to one review.
 *
 * It reads the review out of the same page of the same list the moderator came from — the tab and
 * the page number travel in the URL — so a reload lands on the same review rather than on an
 * empty form, and Back returns to the row it was opened from.
 */
export function ReviewReplyPage() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();

  const status = params.get('status') ?? 'Pending';
  const page = Number(params.get('page')) || 1;

  const reviews = useReviews({ status: status === 'all' ? '' : status, page });
  const reply = useReplyToReview();

  const review = (reviews.data?.items ?? []).find((item) => String(item.id) === id);

  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  // The page of reviews arrives a beat after the form does; fill it once it has.
  useEffect(() => {
    if (review) {
      setText(review.reply ?? '');
    }
  }, [review]);

  const back = () => navigate({ pathname: '/reviews', search: params.toString() });

  if (reviews.isPending) {
    return <Skeleton height="420px" />;
  }

  if (!review) {
    return (
      <EmptyState
        icon="star"
        title={t('reviews.none')}
        action={
          <button type="button" className="awj-button awj-button--secondary" onClick={back}>
            {t('common.back')}
          </button>
        }
        tall
      />
    );
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    if (!review) {
      return;
    }

    try {
      await reply.mutateAsync({ id: review.id, reply: text });
      toast.show({ title: t('reviews.replied'), tone: 'success' });
      back();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? (caught.problem.detail ?? t('reviews.couldNotReply'))
          : t('common.somethingWentWrong'),
      );
    }
  }

  return (
    <FormPage
      icon="reviews"
      title={t('reviews.replyTitle')}
      subtitle={t('reviews.replyHint')}
      onBack={back}
      onSubmit={submit}
      submitLabel={t('common.save')}
      submitting={reply.isPending}
    >
      <div className="category-form">
        {/* What is being answered, so the reply is written to the review rather than from memory. */}
        <div className="reply-source card">
          <p className="reply-source__meta">
            <bdi>{review.authorName}</bdi>
            {' · '}
            <bdi>{formatDate(locale, review.createdAt)}</bdi>
            {' · '}
            <span aria-label={t('customers.rating', { rating: String(review.rating) })}>
              <span aria-hidden="true">
                {'★'.repeat(review.rating)}
                {'☆'.repeat(5 - review.rating)}
              </span>
            </span>
          </p>
          {review.title ? (
            <p className="reply-source__title">
              <bdi>{review.title}</bdi>
            </p>
          ) : null}
          <blockquote className="inbox__quote">
            <bdi>{review.body}</bdi>
          </blockquote>
        </div>

        <div className="field">
          <FieldHead
            htmlFor="review-reply"
            label={t('reviews.reply')}
            hint={t('reviews.replyHint')}
            hintId="review-reply-hint"
          />
          <textarea
            id="review-reply"
            className="awj-textarea"
            rows={6}
            value={text}
            aria-describedby="review-reply-hint"
            onChange={(event) => setText(event.target.value)}
          />
        </div>

        {error ? (
          <p className="bilingual__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </FormPage>
  );
}
